import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Users, BarChart, CheckCircle2, Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import Footer from "@/components/Footer";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { authService } from "@/services/authService";
import { Link } from "react-router-dom";

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const { theme } = useTheme();
  const { login, register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [isResendCase, setIsResendCase] = useState(false); // Track if this is a resend case
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendModalLoading, setResendModalLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // Cooldown in seconds

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Cooldown timer for resend email
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Sign in form state
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");

  // Sign up form state
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpName, setSignUpName] = useState("");

  // Password visibility state
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);
    try {
      await login(signInEmail, signInPassword);
      toast.success("Welcome back!");
    } catch (error: any) {
      // Check if error is due to email not verified
      if (error.response?.data?.code === 'EMAIL_NOT_VERIFIED' || error.response?.data?.details?.emailVerified === false) {
        setEmailNotVerified(true);
      } else {
        toast.error(error.response?.data?.message || error.message || "Failed to sign in");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!signInEmail) {
      toast.error("Please enter your email address");
      return;
    }
    if (resendCooldown > 0) {
      return; // Prevent action during cooldown
    }
    setResendLoading(true);
    try {
      await authService.resendVerificationEmail(signInEmail);
      toast.success("Verification email sent! Please check your inbox.");
      setEmailNotVerified(false);
      setResendCooldown(60); // 60 seconds cooldown
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to resend verification email";
      toast.error(errorMessage);
      // If rate limited, set cooldown based on retryAfter if available
      if (error.response?.status === 429 && error.response?.data?.details?.retryAfter) {
        setResendCooldown(Math.ceil(error.response.data.details.retryAfter / 60) * 60); // Convert to minutes then to seconds
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Only send name if it's not empty
      const nameToSend = signUpName.trim() || undefined;
      const response = await register(signUpEmail, signUpPassword, nameToSend);
      setRegistrationEmail(response.email);
      
      // Check if this is a resend case (message contains "already sent")
      const isResend = response.message.toLowerCase().includes('already sent');
      setIsResendCase(isResend);
      
      // Reset cooldown when opening modal
      setResendCooldown(0);
      setShowSuccessModal(true);
      // Switch to signin tab after successful registration
      setActiveTab("signin");
      // Clear form
      setSignUpEmail("");
      setSignUpPassword("");
      setSignUpName("");
    } catch (error: any) {
      // Show detailed validation errors if available
      const errorMessage = error.response?.data?.message || error.message || "Failed to create account";
      const errorDetails = error.response?.data?.details;
      
      if (errorDetails && Array.isArray(errorDetails) && errorDetails.length > 0) {
        const fieldErrors = errorDetails.map((d: any) => `${d.field}: ${d.message}`).join(", ");
        toast.error(fieldErrors);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerGoogleSignIn = () => {
    setGoogleLoading(true);
    // Redirect to backend OAuth endpoint which will redirect to Google
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    window.location.href = `${apiBaseUrl}/auth/oauth/google`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col md:flex-row">
        <div className="flex-1 bg-gradient-to-b from-primary/20 to-background p-8 flex flex-col justify-center items-center hidden md:flex">
        <div className="max-w-md space-y-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight">Step</h1>
            <p className="text-xl text-muted-foreground">
              Track your wellbeing and growth journey
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-primary/10 p-2 rounded-full">
                <CheckCircle2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Daily Check-ins</h3>
                <p className="text-muted-foreground text-sm">
                  Track your mood, energy, and progress with simple daily
                  reflections
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-primary/10 p-2 rounded-full">
                <BarChart className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Personal Insights</h3>
                <p className="text-muted-foreground text-sm">
                  Gain valuable insights into your patterns and trends over time
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-primary/10 p-2 rounded-full">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Team Collaboration</h3>
                <p className="text-muted-foreground text-sm">
                  Connect with your team and support each other's growth journey
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-primary/10 p-2 rounded-full">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Calendar Integration</h3>
                <p className="text-muted-foreground text-sm">
                  Sync with your preferred calendar to maintain work-life
                  balance
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-primary"></div>
            </div>
            <CardTitle className="text-2xl">Welcome to Step</CardTitle>
            <CardDescription>
              Your personal growth and wellbeing companion
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as "signin" | "signup")}
            >
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-password"
                        type={showSignInPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowSignInPassword(!showSignInPassword)}
                      >
                        {showSignInPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <Link
                        to="/forgot-password"
                        className="text-primary hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>
                  {emailNotVerified && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Email Not Verified</AlertTitle>
                      <AlertDescription className="mt-2">
                        <p className="mb-2">
                          Please verify your email before signing in. A verification email has been sent.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleResendVerification}
                          disabled={resendLoading || resendCooldown > 0}
                        >
                          {resendLoading 
                            ? "Sending..." 
                            : resendCooldown > 0 
                              ? `Resend (${resendCooldown}s)`
                              : "Resend Verification Email"}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={triggerGoogleSignIn}
                    disabled={googleLoading || loading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1-3.71 1-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    {googleLoading ? "Signing in..." : "Sign in with Google"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={signUpName}
                      onChange={(e) => setSignUpName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={signUpEmail}
                        onChange={(e) => setSignUpEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        type={showSignUpPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        value={signUpPassword}
                        onChange={(e) => setSignUpPassword(e.target.value)}
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                      >
                        {showSignUpPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Min. 8 characters with uppercase, lowercase, and number
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating account..." : "Sign Up"}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={triggerGoogleSignIn}
                    disabled={googleLoading || loading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1-3.71 1-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    {googleLoading ? "Signing up..." : "Sign up with Google"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              <p>
                By continuing, you agree to our{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  className="underline hover:text-foreground"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  className="underline hover:text-foreground"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <p className="mt-2 text-xs">
                Sessions expire after 1 hour of inactivity for security.
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>
      </div>
      <Footer />

      {/* Success Modal after Signup */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check Your Email</DialogTitle>
            <DialogDescription>
              {isResendCase ? (
                <>
                  A verification email was already sent to <strong>{registrationEmail}</strong>. 
                  Please check your email or click resend if you didn't receive it.
                </>
              ) : (
                <>
                  We've sent a verification email to <strong>{registrationEmail}</strong>. 
                  Please click the link in the email to verify your account before signing in.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={async () => {
                if (!registrationEmail) return;
                if (resendCooldown > 0) return; // Prevent action during cooldown
                setResendModalLoading(true);
                try {
                  await authService.resendVerificationEmail(registrationEmail);
                  toast.success("Verification email sent! Please check your inbox.");
                  setIsResendCase(false); // After resend, it's a new email
                  setResendCooldown(60); // 60 seconds cooldown
                  setShowSuccessModal(false); // Close modal after successful resend
                } catch (error: any) {
                  const errorMessage = error.response?.data?.message || error.message || "Failed to resend verification email";
                  toast.error(errorMessage);
                  // If rate limited, set cooldown based on retryAfter if available
                  if (error.response?.status === 429 && error.response?.data?.details?.retryAfter) {
                    setResendCooldown(Math.ceil(error.response.data.details.retryAfter / 60) * 60); // Convert to minutes then to seconds
                  }
                  // Don't close modal on error so user can try again
                } finally {
                  setResendModalLoading(false);
                }
              }}
              disabled={resendModalLoading || resendCooldown > 0}
            >
              {resendModalLoading 
                ? "Sending..." 
                : resendCooldown > 0 
                  ? `Resend Email (${resendCooldown}s)`
                  : "Resend Email"}
            </Button>
            <Button onClick={() => setShowSuccessModal(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
